import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * iter 187.b - Canonical vs raw user count tile.
 *
 * Green tick when the raw user count equals the canonical (real-user-
 * filtered) count AND every test-pattern email carries the is_test
 * flag. Any drift bubbles up here so the iter 187 audit + purge never
 * has to run again by hand.
 */
export default function CanonicalCountTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchStatus = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/pollution-status`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  if (loading && !data) {
    return (
      <div
        data-testid="canonical-count-tile-loading"
        className="rounded-lg border border-[#EFE6CF] bg-white p-5 text-sm text-[#0A1F2E]/70"
      >
        Loading pollution status…
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="canonical-count-tile-error"
        className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800"
      >
        Failed to load pollution status: {err}
      </div>
    );
  }
  if (!data?.available) {
    return null;
  }

  const healthy = !!data.healthy;
  const {
    raw_users: raw,
    canonical_users: canonical,
    delta,
    buckets = {},
    drift = {},
  } = data;

  const driftMissing = drift.test_email_missing_is_test_flag || 0;

  return (
    <div
      data-testid="canonical-count-tile"
      className={`rounded-lg border p-5 text-sm ${
        healthy
          ? 'border-emerald-200 bg-emerald-50/60'
          : 'border-amber-300 bg-amber-50'
      }`}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {healthy ? (
            <CheckCircle2
              className="h-5 w-5 text-emerald-600"
              data-testid="canonical-count-tile-status-ok"
            />
          ) : (
            <AlertTriangle
              className="h-5 w-5 text-amber-600"
              data-testid="canonical-count-tile-status-drift"
            />
          )}
          <span
            className="text-[11px] uppercase tracking-[0.18em] font-mono text-[#1A5C6E]"
          >
            Test-Pollution Guard
          </span>
        </div>
        <button
          type="button"
          onClick={fetchStatus}
          disabled={loading}
          data-testid="canonical-count-tile-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-[#1A5C6E] hover:text-[#0A1F2E]"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex items-baseline gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#0A1F2E]/60 font-mono">
            Canonical (real users)
          </p>
          <p
            data-testid="canonical-count-tile-canonical"
            className="text-3xl font-semibold text-[#0A1F2E]"
          >
            {canonical}
          </p>
        </div>
        <div className="text-[#0A1F2E]/50">/</div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#0A1F2E]/60 font-mono">
            Raw (total in DB)
          </p>
          <p
            data-testid="canonical-count-tile-raw"
            className="text-3xl font-semibold text-[#0A1F2E]"
          >
            {raw}
          </p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-[10px] uppercase tracking-widest text-[#0A1F2E]/60 font-mono">
            Excluded
          </p>
          <p
            data-testid="canonical-count-tile-delta"
            className={`text-2xl font-semibold ${
              healthy ? 'text-emerald-700' : 'text-amber-700'
            }`}
          >
            −{delta}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#0A1F2E]/75 sm:grid-cols-5">
        <span>
          Ambassadors: <b data-testid="canonical-count-tile-b-amb">{buckets.is_ambassador ?? 0}</b>
        </span>
        <span>
          Internal: <b data-testid="canonical-count-tile-b-int">{buckets.is_internal ?? 0}</b>
        </span>
        <span>
          Admins: <b data-testid="canonical-count-tile-b-admin">{buckets.role_admin ?? 0}</b>
        </span>
        <span>
          Test-email: <b data-testid="canonical-count-tile-b-test">{buckets.test_email ?? 0}</b>
        </span>
        <span>
          is_test flag: <b data-testid="canonical-count-tile-b-flag">{buckets.is_test_flag ?? 0}</b>
        </span>
      </div>

      {!healthy && (
        <div
          data-testid="canonical-count-tile-drift-msg"
          className="mt-4 rounded border border-amber-300 bg-white/60 px-3 py-2 text-xs text-amber-900"
        >
          <b>{driftMissing}</b> account
          {driftMissing === 1 ? ' has' : 's have'} a test-pattern email but no{' '}
          <code className="rounded bg-amber-100 px-1">is_test</code> flag. A
          signup path skipped <code className="rounded bg-amber-100 px-1">mark_test_flag</code>.
          Run the iter 187 archive script or fix the offending route.
        </div>
      )}
    </div>
  );
}
